// Declare gtag as a global function
declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
  }
}

// Analytics event names
export const AnalyticsEvent = {
  PAGE_VIEW: 'page_view',
  SCREEN_VIEW: 'screen_view',
  PREDICTION_VIEW: 'prediction_view',
  METHODOLOGY_VIEW: 'methodology_view',
  API_ERROR: 'api_error',
  API_SUCCESS: 'api_success',
  FIGHTER_CARD_CLICK: 'fighter_card_click',
  BUTTON_CLICK: 'button_click'
} as const;

// Analytics utility functions
export const analytics = {
  // Initialize analytics with user properties
  init: () => {
    if (typeof window.gtag !== 'undefined') {
      // Set default user properties
      window.gtag('set', {
        // Device category (mobile, tablet, desktop)
        device_category: /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
          ? 'mobile' 
          : 'desktop',
        // Browser
        browser: navigator.userAgent,
        // Screen size
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        // Viewport size
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        // Language
        language: navigator.language
      });
    }
  },

  // Track page views with enhanced data
  trackPageView: (path: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: path,
        page_title: document.title,
        // Include device info
        device_category: /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
          ? 'mobile' 
          : 'desktop',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`
      });
    }
  },

  // Track fighter card clicks
  trackFighterCardClick: (fighterName: string, cardType: 'prediction' | 'history' | 'stats') => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'fighter_card_click', {
        fighter_name: fighterName,
        card_type: cardType,
        page_location: window.location.pathname
      });
    }
  },

  // Track button clicks
  trackButtonClick: (buttonName: string, buttonLocation: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'button_click', {
        button_name: buttonName,
        button_location: buttonLocation,
        page_location: window.location.pathname
      });
    }
  },

  // Track prediction views with enhanced data
  trackPredictionView: (fightName: string, predictionConfidence: number) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'prediction_view', {
        fight_name: fightName,
        content_type: 'prediction',
        prediction_confidence: predictionConfidence,
        device_category: /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
          ? 'mobile' 
          : 'desktop'
      });
    }
  },

  // Track methodology views
  trackMethodologyView: () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'methodology_view', {
        content_type: 'methodology',
        device_category: /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
          ? 'mobile' 
          : 'desktop'
      });
    }
  },

  // Track API errors
  trackApiError: (error: Error) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'api_error', {
        error_message: error.message,
        error_type: 'api',
        page_location: window.location.pathname
      });
    }
  },

  // Track API successes
  trackApiSuccess: (endpoint: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'api_success', {
        endpoint: endpoint,
        status: 'success',
        page_location: window.location.pathname
      });
    }
  },

  // Custom event tracking
  trackEvent: (eventName: string, parameters?: Record<string, any>) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', eventName, {
        ...parameters,
        device_category: /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
          ? 'mobile' 
          : 'desktop',
        page_location: window.location.pathname
      });
    }
  }
}; 